// src/components/AdminHome.js
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs, getCountFromServer, limit, startAfter } from 'firebase/firestore';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import format from 'date-fns/format';
import subDays from 'date-fns/subDays';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import startOfDay from 'date-fns/startOfDay';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';

function AdminHome() {
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalVideos, setTotalVideos] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [totalShares, setTotalShares] = useState(0);
  const [activatedUsers, setActivatedUsers] = useState(0);
  const [avgTakes, setAvgTakes] = useState(0);
  const [recentEvents, setRecentEvents] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [activityLoading, setActivityLoading] = useState(false);
  const [userSignupsData, setUserSignupsData] = useState([]);
  const [videoActivityData, setVideoActivityData] = useState([]);
  const [engagementData, setEngagementData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userChartTimespan, setUserChartTimespan] = useState(14);
  const [videoChartTimespan, setVideoChartTimespan] = useState(14);
  const [engagementTimespan, setEngagementTimespan] = useState(14);
  const [chartsLoading, setChartsLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
    fetchRecentActivity(false);
  }, []);

  useEffect(() => {
    fetchUserSignupsData(userChartTimespan);
  }, [userChartTimespan]);

  useEffect(() => {
    fetchVideoActivityData(videoChartTimespan);
  }, [videoChartTimespan]);

  useEffect(() => {
    fetchEngagementData(engagementTimespan);
  }, [engagementTimespan]);

  const fetchRecentActivity = async (isLoadMore = false) => {
    if (activityLoading) return;
    setActivityLoading(true);
    
    try {
      const eventsRef = collection(db, 'video_events');
      let q;
      
      const pageSize = 100;

      if (isLoadMore && lastVisible) {
        q = query(
          eventsRef, 
          orderBy('timestamp', 'desc'), 
          startAfter(lastVisible),
          limit(pageSize)
        );
      } else {
        q = query(eventsRef, orderBy('timestamp', 'desc'), limit(pageSize));
      }

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.docs.length > 0) {
        const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
        setLastVisible(newLastVisible);
        setHasMore(querySnapshot.docs.length === pageSize);
      } else {
        setHasMore(false);
      }

      const events = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        time: doc.data().timestamp?.toDate()
      }));

      if (isLoadMore) {
        setRecentEvents(prev => [...prev, ...events]);
      } else {
        setRecentEvents(events);
      }
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      toast.error('Failed to load activity.');
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const usersCollection = collection(db, 'users');
      const videosCollection = collection(db, 'videos');
      const eventsCollection = collection(db, 'video_events');

      const [usersSnap, videosSnap, viewsSnap, sharesSnap, activatedSnap, startsSnap, rerecordsSnap] = await Promise.all([
        getCountFromServer(usersCollection),
        getCountFromServer(videosCollection),
        getCountFromServer(query(eventsCollection, where('eventType', '==', 'view'))),
        getCountFromServer(query(eventsCollection, where('eventType', '==', 'share'))),
        getCountFromServer(query(usersCollection, where('videoCount', '>', 0))),
        getCountFromServer(query(eventsCollection, where('eventType', '==', 'record_start'))),
        getCountFromServer(query(eventsCollection, where('eventType', '==', 'rerecord')))
      ]);

      const usersCount = usersSnap.data().count;
      const videosCount = videosSnap.data().count;
      const startsCount = startsSnap.data().count;
      const rerecordsCount = rerecordsSnap.data().count;

      setTotalUsers(usersCount);
      setTotalVideos(videosCount);
      setTotalViews(viewsSnap.data().count);
      setTotalShares(sharesSnap.data().count);
      setActivatedUsers(activatedSnap.data().count);
      
      // Calculate Average Takes (Attempts / Successes)
      const totalAttempts = startsCount + rerecordsCount;
      setAvgTakes(videosCount > 0 ? (totalAttempts / videosCount).toFixed(1) : 0);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Failed to fetch metrics.');
      setLoading(false);
    }
  };

  const fetchUserSignupsData = async (days) => {
    try {
      setChartsLoading(true);
      const endDate = new Date();
      const startDate = subDays(endDate, days);
      const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
      
      const usersByDate = {};
      dateRange.forEach(date => {
        const dateStr = format(date, 'MM/dd');
        usersByDate[dateStr] = { date: dateStr, none: 0, sixMonth: 0, lifetime: 0, total: 0 };
      });

      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('createdAt', '>=', startDate), orderBy('createdAt', 'asc'));
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach(doc => {
        const userData = doc.data();
        if (userData.createdAt) {
          const dateStr = format(userData.createdAt.toDate(), 'MM/dd');
          const tier = userData.subscriptionTier || 'none';
          if (usersByDate[dateStr]) {
            usersByDate[dateStr][tier] += 1;
            usersByDate[dateStr].total += 1;
          }
        }
      });
      
      setUserSignupsData(Object.values(usersByDate));
      setChartsLoading(false);
    } catch (error) {
      console.error('Error fetching signups:', error);
      setChartsLoading(false);
    }
  };

  const fetchVideoActivityData = async (days) => {
    try {
      setChartsLoading(true);
      const endDate = new Date();
      const startDate = subDays(endDate, days);
      const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
      
      const videosByDate = {};
      dateRange.forEach(date => {
        const dateStr = format(date, 'MM/dd');
        videosByDate[dateStr] = { date: dateStr, uploads: 0, deletions: 0, net: 0, cumulative: 0 };
      });

      const videosRef = collection(db, 'videos');
      const uploadsQuery = query(videosRef, where('createdAt', '>=', startDate), orderBy('createdAt', 'asc'));
      const uploadsSnapshot = await getDocs(uploadsQuery);
      
      uploadsSnapshot.forEach(doc => {
        const videoData = doc.data();
        if (videoData.createdAt) {
          const dateStr = format(videoData.createdAt.toDate(), 'MM/dd');
          if (videosByDate[dateStr]) videosByDate[dateStr].uploads += 1;
        }
      });

      try {
        const videoEventsRef = collection(db, 'video_events');
        const deletionsQuery = query(videoEventsRef, where('eventType', '==', 'deletion'), where('timestamp', '>=', startDate), orderBy('timestamp', 'asc'));
        const deletionsSnapshot = await getDocs(deletionsQuery);
        deletionsSnapshot.forEach(doc => {
          const dateStr = format(doc.data().timestamp.toDate(), 'MM/dd');
          if (videosByDate[dateStr]) videosByDate[dateStr].deletions += 1;
        });
      } catch (e) {}

      const chartData = Object.values(videosByDate);
      let runningTotal = 0;
      chartData.forEach(day => {
        day.deletions = -Math.abs(day.deletions);
        day.net = day.uploads + day.deletions;
        runningTotal += day.net;
        day.cumulative = runningTotal;
      });
      
      setVideoActivityData(chartData);
      setChartsLoading(false);
    } catch (error) {
      console.error('Error fetching activity:', error);
      setChartsLoading(false);
    }
  };

  const fetchEngagementData = async (days) => {
    try {
      setChartsLoading(true);
      const endDate = new Date();
      const startDate = subDays(endDate, days);
      const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
      
      const engagementByDate = {};
      dateRange.forEach(date => {
        const dateStr = format(date, 'MM/dd');
        engagementByDate[dateStr] = { date: dateStr, views: 0, shares: 0, recordings: 0, rerecords: 0 };
      });

      const eventsRef = collection(db, 'video_events');
      const q = query(eventsRef, where('timestamp', '>=', startDate), orderBy('timestamp', 'asc'));
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach(doc => {
        const event = doc.data();
        if (event.timestamp) {
          const dateStr = format(event.timestamp.toDate(), 'MM/dd');
          if (engagementByDate[dateStr]) {
            if (event.eventType === 'view') engagementByDate[dateStr].views += 1;
            if (event.eventType === 'share') engagementByDate[dateStr].shares += 1;
            if (event.eventType === 'record_start') engagementByDate[dateStr].recordings += 1;
            if (event.eventType === 'rerecord') engagementByDate[dateStr].rerecords += 1;
          }
        }
      });
      
      setEngagementData(Object.values(engagementByDate));
      setChartsLoading(false);
    } catch (error) {
      console.error('Error fetching engagement:', error);
      setChartsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <ClipLoader size={50} color="#123abc" loading={loading} />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Admin Overview</h2>

        {/* Primary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white shadow rounded-lg p-6 border-l-4 border-blue-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Total Users</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900">{totalUsers}</p>
            <p className="mt-1 text-xs text-gray-500">
              {totalUsers > 0 ? ((activatedUsers / totalUsers) * 100).toFixed(1) : 0}% activation rate
            </p>
          </div>

          <div className="bg-white shadow rounded-lg p-6 border-l-4 border-green-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Total Videos</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900">{totalVideos}</p>
            <p className="mt-1 text-xs text-gray-500">Avg {avgTakes} takes per video</p>
          </div>

          <div className="bg-white shadow rounded-lg p-6 border-l-4 border-purple-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Total Views</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900">{totalViews}</p>
            <p className="mt-1 text-xs text-gray-500">Public landing pages</p>
          </div>

          <div className="bg-white shadow rounded-lg p-6 border-l-4 border-orange-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Total Shares</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900">{totalShares}</p>
            <p className="mt-1 text-xs text-gray-500">Link & Email copies</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Engagement Chart */}
          <div className="lg:col-span-2 bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Engagement Activity</h3>
              <div className="flex space-x-2">
                {[7, 14, 30].map(d => (
                  <button 
                    key={d}
                    onClick={() => setEngagementTimespan(d)}
                    className={`px-3 py-1 text-xs rounded ${engagementTimespan === d ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    {d}D
                  </button>
                ))}
              </div>
            </div>
            
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={engagementData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="views" stroke="#8884d8" name="Views" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="shares" stroke="#82ca9d" name="Shares" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="recordings" stroke="#ffc658" name="Starts" dot={false} />
                  <Line type="monotone" dataKey="rerecords" stroke="#ff7675" name="Re-records" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Activity Feed */}
          <div className="bg-white shadow rounded-lg p-6 flex flex-col h-[500px]">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Recent Activity</h3>
            <div className="space-y-4 overflow-y-auto flex-grow pr-2">
              {recentEvents.length === 0 && !activityLoading ? (
                <p className="text-gray-500 text-sm italic">No recent activity found.</p>
              ) : (
                <>
                  {recentEvents.map((event) => (
                    <div key={event.id} className="text-sm border-b border-gray-100 pb-2">
                      <div className="flex justify-between items-start">
                        <span className={`font-medium ${
                          event.eventType === 'view' ? 'text-purple-600' :
                          event.eventType === 'share' ? 'text-orange-600' :
                          event.eventType === 'rerecord' ? 'text-red-500' :
                          'text-blue-600'
                        }`}>
                          {event.eventType.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-400">
                          {event.time ? formatDistanceToNow(event.time, { addSuffix: true }) : 'just now'}
                        </span>
                      </div>
                      <p className="text-gray-600 text-xs mt-1 truncate">
                        {event.eventType === 'view' ? `Video: ${event.videoId?.substring(0, 8)}...` : 
                         event.eventType === 'share' ? `via ${event.copyType}` :
                         `User: ${event.userId?.substring(0, 8)}...`}
                      </p>
                    </div>
                  ))}
                  
                  {activityLoading && (
                    <div className="flex justify-center py-4">
                      <ClipLoader size={20} color="#123abc" />
                    </div>
                  )}

                  {hasMore && !activityLoading && (
                    <button 
                      onClick={() => fetchRecentActivity(true)}
                      className="w-full py-2 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                    >
                      Load More Activity
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* User Signups Chart */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">User Signups</h3>
            <div className="flex space-x-2">
              <button onClick={() => setUserChartTimespan(7)} className={`px-3 py-1 rounded ${userChartTimespan === 7 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>7 Days</button>
              <button onClick={() => setUserChartTimespan(14)} className={`px-3 py-1 rounded ${userChartTimespan === 14 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>14 Days</button>
              <button onClick={() => setUserChartTimespan(30)} className={`px-3 py-1 rounded ${userChartTimespan === 30 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>30 Days</button>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={userSignupsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="total" stackId="1" stroke="#8884d8" fill="#8884d8" name="Total" />
                <Area type="monotone" dataKey="none" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="No Subscription" />
                <Area type="monotone" dataKey="sixMonth" stackId="2" stroke="#ffc658" fill="#ffc658" name="Six Month" />
                <Area type="monotone" dataKey="lifetime" stackId="2" stroke="#ff8042" fill="#ff8042" name="Lifetime" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Video Activity Chart */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Video Activity</h3>
            <div className="flex space-x-2">
              <button onClick={() => setVideoChartTimespan(7)} className={`px-3 py-1 rounded ${videoChartTimespan === 7 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>7 Days</button>
              <button onClick={() => setVideoChartTimespan(14)} className={`px-3 py-1 rounded ${videoChartTimespan === 14 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>14 Days</button>
              <button onClick={() => setVideoChartTimespan(30)} className={`px-3 py-1 rounded ${videoChartTimespan === 30 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>30 Days</button>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={videoActivityData} barCategoryGap={12}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip formatter={(value, name) => name === 'Deletions' ? [Math.abs(value), name] : [value, name]} />
                <Legend />
                <Bar yAxisId="left" dataKey="uploads" fill="#82ca9d" name="Uploads" stackId="stack" />
                <Bar yAxisId="left" dataKey="deletions" fill="#ff7675" name="Deletions" stackId="stack" barSize={20} />
                <Line yAxisId="right" type="monotone" dataKey="cumulative" stroke="#ff7300" name="Net Total" dot={true} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminHome;
